import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import biClient from "@apis/businessIntelligenceClient";
import useAvailibleMenu from "./useAvailibleMenu";
import { MenuTypeConst, type MenuType, type MenuRoute, type AvailibleMenuData } from "./menuTypes";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import type { DashboardMenuItem } from "@apis/businessIntelligenceClient/businessIntelligenceClient.dto";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";

interface ExtendedDashboardMenuItem extends DashboardMenuItem {
  menuName?: string;
  powerBiReportType?: string;
}

interface ExtendedMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
  reportType?: string;
}

/**
 * Return type of useAllMenu hook
 */
interface AllMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: MenuRoute[];
}

/**
 * Hook for managing the "All" menu routes (cross-org view shown when the user
 * picks the "All" pill in the org selector).
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @returns Menu data including routes, available paths, and loading state
 */
const useAllMenu = (_loginInfo: LoginInfo): AllMenuData => {
  const global = useSelector((state: RootState) => state.all);

  const initialRoutes: ExtendedMenuRoute[] = [
    {
      collapse: false,
      view: true,
      name: translate(LanguageKey.partner_partners_key),
      state: "partnersDashboard",
      to: "/partners_dashboard",
      layout: "",
      icon: <span className="partner-admin-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.system_key),
      state: "systemDashboard",
      to: "/system_dashboard",
      layout: "",
      icon: <span className="system-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.finance_landing_key),
      state: "financeDashboard",
      to: "/finance_dashboard",
      layout: "",
      icon: <span className="invoicing-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.support_landing_key),
      state: "supportDashboard",
      to: "/support_dashboard",
      layout: "",
      icon: <span className="support-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.customer_success_landing_key),
      state: "customerSuccessDashboard",
      to: "/customer_success_dashboard",
      layout: "",
      icon: <span className="help-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.sales_landing_key),
      state: "salesDashboard",
      to: "/sales_dashboard",
      layout: "",
      icon: <span className="sales-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      collapse: false,
      view: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
      name: translate(LanguageKey.presales_landing_key),
      state: "presalesDashboard",
      to: "/presales_dashboard",
      layout: "",
      icon: <span className="presales-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      systemOnly: true,
    },
    {
      view:
        hasFeature(FeatureKey.utilization_suez) ||
        (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD)),
      collapse: true,
      name: translate(LanguageKey.UserPermission_MENU_HOME_DASHBOARD_description_key),
      state: "openDashboards",
      views: [],
    },
    {
      collapse: true,
      name: translate(LanguageKey.trackers_and_sensors_key),
      state: "openTrackersSensors",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
            to: "/trackers",
            layout: "",
            name: translate(LanguageKey.sidebar_admin_trackers_key),
            subPages: ["/import_tracker_settings"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
            to: "/ble_sensors",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.bulk_operations) &&
              hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION),
            to: "/bulk_manage",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key),
            subPages: ["/bulk_operations", "/bulk_results_detail_system"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE),
            to: "/device_usage_profile",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
            subPages: ["/device_usage_profile_manage", "/device_usage_profile_compare"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.reports_key),
        state: "openReports",
        views: [
          {
            view:
              hasFeature(FeatureKey.report_generator) &&
              hasPermission(UserPermission.MENU_HOME_REPORT_LIST),
            to: "/reports",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_LIST_description_key),
            subPages: ["/create_report"],
          },
          {
            view:
              hasFeature(FeatureKey.report_generator) &&
              hasPermission(UserPermission.MENU_HOME_REPORT_SCHEDULES),
            to: "/report_schedule",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_SCHEDULES_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_key),
        state: "openOrgSetting",
        views: [
          {
            view:
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE),
            to: "/infrastructures",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
            subPages: ["/import_infrastructure_settings"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_PRIVATE_CELLULAR),
            to: `/private_cellular_all`,
            layout: "",
            name: translate(LanguageKey.UserPermissionCategory_PRIVATE_CELLULAR_title_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organizations_key),
        state: "openOrgSetting",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_LIST),
            to: `/organization`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key),
          },
          {
            view:
              hasFeature(FeatureKey.demo_environment) && hasPermission(UserPermission.MENU_ADMIN_DEMO),
            to: "/org_demo",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
            subPages: ["/org_demo_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.access_management_key),
        state: "openParnerOrg",
        views: [
          {
            view:
              hasFeature(FeatureKey.custom_user_roles) &&
              hasPermission(UserPermission.MENU_ADMIN_USER_ROLE),
            to: "/role_manager",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
            subPages: ["/role_manager_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organisation_billing_key),
        state: "openInsights",
        views: [
          {
            view:
              hasPermission(UserPermission.MENU_ADMIN_QUOTES) &&
              hasFeature(FeatureKey.quote_manage),
            to: "/quotes_storefront",
            layout: "",
            name: translate(LanguageKey.quotes_key),
            subPages: ["/quotes", "/quote_detail"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.customization_key),
        state: "opencustomization",
        views: [
          {
            view:
              hasFeature(FeatureKey.image_library) &&
              hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY),
            to: "/image_library_all",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
            subPages: ["/image_library_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.sidebar_support_key),
        state: "openInsights",
        views: [
          {
            view:
              hasFeature(FeatureKey.stock_movement) &&
              hasPermission(UserPermission.MENU_STOCK_MOVEMENT),
            to: "/stock_movement",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_STOCK_MOVEMENT_description_key),
            subPages: ["/stock_movement_view"],
          },
          {
            view: hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION),
            to: `/rma_declaration`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key),
            subPages: ["/rma_declaration_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.platform_notifications_key),
        state: "openLocation",
        views: [
          {
            view: hasPermission(UserPermission.MENU_ADMIN_NOTIFICATION),
            to: "/notification",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_NOTIFICATION_description_key),
            subPages: ["/notification_update"],
          },
        ],
      },
    ];

  const [routes, setRoutes] = useState<ExtendedMenuRoute[]>(initialRoutes);
  const [firstInit, setFirstInit] = useState(true);
  const [data, setData] = useState<AllMenuData>({
    type: MenuTypeConst.ALL,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/organization",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/organization");

  useEffect(() => {
    if (firstInit) return;
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData, firstInit]);

  useEffect(() => {
    setData({ ...data, loading: true });
    const dashboardViews: ExtendedMenuRoute[] = [
      {
        view: hasFeature(FeatureKey.utilization_suez),
        to: "/suez_dashboard",
        layout: "",
        name: translate(LanguageKey.suez_dashboard_key),
        category: "dashboard",
      },
    ];

    if (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.DASHBOARD_VIEW)) {
      biClient
        .getDashBoardsForMenu({})
        .then((response) => {
          const dashboards = response.data as ExtendedDashboardMenuItem[];

          for (let i = 0; i < dashboards.length; i++) {
            dashboardViews.push({
              view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD),
              to: "/bi_dashboard/" + dashboards[i].id,
              layout: "",
              type: "bi",
              name: dashboards[i].menuName || dashboards[i].name,
              reportType: dashboards[i].powerBiReportType,
              category: "dashboard",
            });
          }
          updateDashBoardViews(dashboardViews);
        })
        .catch(() => {
          updateDashBoardViews(dashboardViews);
        });
    } else {
      updateDashBoardViews(dashboardViews);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global.reloadSidebar]);

  function updateDashBoardViews(dashboardViews: ExtendedMenuRoute[]): void {
    dashboardViews.push({
      view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD),
      to: "/all_dashboards",
      layout: "",
      type: "bi",
      name: translate(LanguageKey.all_dashboards_key),
      category: "dashboard",
    });
    setRoutes((oldRoutes) => {
      const route = oldRoutes.find((x) => x.state === "openDashboards");
      if (route) {
        route.views = [];
        route.views.push(...dashboardViews);
      }
      return [...oldRoutes];
    });
    setFirstInit(false);
  }

  return data;
};

export default useAllMenu;
